import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { downloadSupplyOrderIntegrationFile, previewSupplyOrderIntegration } from '../customizations/customizationApi';
import { SupplyOrderIntegrationPreviewPanels } from '../customizations/SupplyOrderIntegrationPreviewPanels';
import {
  buildSupplyOrderIntegrationDefinition, FIELD_MAP_LANE, MAPPING_PLAN_LANE, OUTPUT_DISPATCH, OUTPUTS, parseFieldMap, parseMappingPlan,
  parseProductMappings, PRODUCT_MAPPINGS_LANE, SAMPLES_LANE, withProductMappingsDraft,
} from '../customizations/supplyOrderIntegrationDefinition';
import { dispatchHashOf, FieldMapTable, isFieldMapStale } from '../customizations/FieldMapTable';
import { SUGGEST_MAPPINGS_INTENT, suggestMappingsPrompt, VALIDATE_MAPPINGS_INTENT, validateMappingsPrompt } from '../customizations/supplyOrderMappingPrompts';
import { AccentSpinner } from '../AccentSpinner';
import { accentUtils, t } from '../../services/HelperService';

// Loaded when the Products tab is first shown, not with this module. This preview is reached from
// queryShapingSessionProfiles, which the job screens import early - while HelperService is still initialising (it pulls
// the product configurator in). The panel's CSV helper leads through ProductSignals / ConfigurationFields to FabricView,
// whose top-level constants read HelperService's measurementUnits: an eager import evaluated that before HelperService
// finished ("Cannot access 'measurementUnits' before initialization"). A render-time import resolves after everything is up.
const SupplyOrderProductMappingsPanel = React.lazy(() =>
  import('../customizations/SupplyOrderProductMappingsPanel').then((m) => ({ default: m.SupplyOrderProductMappingsPanel })));

const safeString = (v) => (v ?? '').toString();

/** The session's outputs as definition outputs[] (known names only, blank scripts dropped). */
export const readIntegrationOutputs = (outputs) => (Array.isArray(outputs) ? outputs : [])
  .map((o) => ({ name: safeString(o?.name).trim(), script: safeString(o?.script) }))
  .filter((o) => OUTPUTS.some((n) => n.toLowerCase() === o.name.toLowerCase()) && o.script.trim());

/** The session's non-blank queries as definition queries[] entries ({ name, query }). */
export const readIntegrationQueries = (queries) => (Array.isArray(queries) ? queries : [])
  .filter((q) => safeString(q?.name).trim() && safeString(q?.query).trim())
  .map((q) => ({ name: safeString(q.name).trim(), query: safeString(q.query) }));

/**
 * Preview tab of the supplyOrderIntegration assistant: runs the session's five scripts, queries and sample replies for
 * the editor's test supply job (or the contract's sample order when none is chosen) through the SAME runtime the
 * editor's Preview and the sender use (SupplyOrderIntegration_Preview), and shows the three panels - the files
 * produced (downloadable), the send plan per transport, and orderResponse replayed over every sample.
 * Nothing is sent; the data store rows the dataStore script requests are prefetched (the sample order reads each
 * request's sample value) and encrypted rows stay opaque tokens.
 *
 * Runs automatically when an artifact changes once the chat turn is over (the server already tested the candidate
 * during the turn; this is the user's copy) and on demand through Run. Failures per output are reported to the dialog
 * (onOutputRendered) so the output badges and the OK guard see them, and pushed to the transcript (onDefect) so the
 * next prompt can quote them.
 *
 * Three tabs: RUN (the files, send plan and replies), MAPPING (the field-mapping table) and PRODUCTS - the editor's Products
 * tab mounted on the session's live lanes, so the plan the assistant records and the worksheet it fills show turn by turn
 * while the chat is read. A pick or an ignore made there writes the worksheet lane (`onLaneArtifactsChange`) and reaches the
 * editor on close like the assistant's own updates; its Suggest / Validate buttons post into the chat (`onSendPrompt`).
 */
export const SupplyOrderIntegrationPreview = ({ outputs, queries, laneArtifacts, busy, hostContext, onDefect, onOutputRendered, onLaneArtifactsChange, onSendPrompt }) => {
  const recordID = Number(hostContext?.recordID) || 0;
  const customizationID = Number(hostContext?.customizationID) || 0;
  const supplierID = Number(hostContext?.supplierID) || 0;
  const supplierName = safeString(hostContext?.supplierName);
  const sessionOutputs = React.useMemo(() => readIntegrationOutputs(outputs), [outputs]);
  const sessionQueries = React.useMemo(() => readIntegrationQueries(queries), [queries]);
  const samplesJson = safeString(laneArtifacts?.[SAMPLES_LANE]) || '[]';
  const fieldMapJson = safeString(laneArtifacts?.[FIELD_MAP_LANE]);
  const fieldMap = React.useMemo(() => parseFieldMap(fieldMapJson), [fieldMapJson]);
  const mappingPlanJson = safeString(laneArtifacts?.[MAPPING_PLAN_LANE]);
  const mappingPlan = React.useMemo(() => parseMappingPlan(mappingPlanJson), [mappingPlanJson]);
  const productMappingsJson = safeString(laneArtifacts?.[PRODUCT_MAPPINGS_LANE]);
  const productMappings = React.useMemo(() => parseProductMappings(productMappingsJson), [productMappingsJson]);

  // what the run and the downloads use: the scripts, queries, samples, mapping and plan - everything the files depend on.
  // The worksheet is deliberately left out: a pick on the Products tab must not re-run the files (they do not read it).
  const definition = React.useMemo(() => buildSupplyOrderIntegrationDefinition({
    existing: hostContext?.definitionBase ? JSON.stringify(hostContext.definitionBase) : null,
    outputs: sessionOutputs,
    queries: sessionQueries,
    samples: samplesJson,
    testRecord: recordID,
    fieldMap,
    mappingPlan,
  }), [fieldMap, hostContext, mappingPlan, recordID, samplesJson, sessionOutputs, sessionQueries]);
  // what the Products tab's check reads: the same plus the worksheet (its ignores turn a gap into "ignored")
  const checkDefinition = React.useMemo(() => buildSupplyOrderIntegrationDefinition({ existing: definition, productMappings }), [definition, productMappings]);
  const checkDefinitionRef = React.useRef(checkDefinition);
  checkDefinitionRef.current = checkDefinition;
  const getDefinition = React.useCallback(() => checkDefinitionRef.current, []);

  const [tab, setTab] = React.useState('run');
  const canEditWorksheet = !!onLaneArtifactsChange;
  const writeWorksheet = React.useCallback((productID, draft) => {
    const next = withProductMappingsDraft(parseProductMappings(productMappingsJson), productID, draft);
    onLaneArtifactsChange?.({ [PRODUCT_MAPPINGS_LANE]: JSON.stringify(next) });
  }, [onLaneArtifactsChange, productMappingsJson]);

  // the mapping's staleness against the session's dispatch script (hash computed like the server's dispatchHash stamp)
  const dispatchScript = sessionOutputs.find((o) => o.name.toLowerCase() === OUTPUT_DISPATCH)?.script ?? '';
  const [dispatchHash, setDispatchHash] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    dispatchHashOf(dispatchScript).then((h) => { if (!cancelled) setDispatchHash(h); }).catch(() => { if (!cancelled) setDispatchHash(null); });
    return () => { cancelled = true; };
  }, [dispatchScript]);
  const fieldMapStale = isFieldMapStale(fieldMap, dispatchHash);

  const [result, setResult] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ranDefinition, setRanDefinition] = React.useState(null);

  const onDefectRef = React.useRef(onDefect);
  onDefectRef.current = onDefect;
  const onOutputRenderedRef = React.useRef(onOutputRendered);
  onOutputRenderedRef.current = onOutputRendered;

  const run = React.useCallback(async (e) => {
    try {
      setError('');
      if (sessionOutputs.length === 0) {
        setError(t('The session has no scripts yet.'));
        return;
      }
      setRunning(true);
      const res = await previewSupplyOrderIntegration({ customizationID, definition, recordID });
      setResult(res);
      setRanDefinition(definition);
      const errors = res?.Errors ?? res?.errors ?? {};
      const failures = [];
      sessionOutputs.forEach((o) => {
        const key = Object.keys(errors).find((k) => k.toLowerCase() === o.name.toLowerCase());
        const list = key === undefined ? [] : (errors[key] ?? []);
        if (list.length > 0) failures.push(`${o.name}: ${list.join('; ')}`);
        onOutputRenderedRef.current?.(o.name, list.length === 0, list.length === 0 ? '' : list.join('\n'));
      });
      if (failures.length > 0) onDefectRef.current?.(`${t('Preview run reported')}:\n${failures.join('\n')}`);
    } catch (ex) {
      const message = safeString(ex?.message) || t('Preview failed.');
      setError(message);
      setRanDefinition(definition);
      onDefectRef.current?.(`${t('Preview run failed')}: ${message}`);
      sessionOutputs.forEach((o) => onOutputRenderedRef.current?.(o.name, false, `${t('Preview failed')}: ${message}`));
    } finally {
      setRunning(false);
      e?.promise?.();
    }
  }, [customizationID, definition, recordID, sessionOutputs]);

  // auto-run changed artifacts once the turn is over
  React.useEffect(() => {
    if (busy || running) return;
    if (sessionOutputs.length === 0) return;
    if (ranDefinition === definition) return;
    run();
  }, [busy, definition, ranDefinition, run, running, sessionOutputs.length]);

  const download = React.useCallback(async (file) => {
    const fileName = safeString(file?.name ?? file?.Name);
    if (!fileName) return;
    try {
      const blob = await downloadSupplyOrderIntegrationFile({ customizationID, definition, recordID, fileName });
      const a = document.createElement('a');
      a.href = blob.url;
      a.download = blob.fileName || fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blob.url), 10000);
    } catch (ex) {
      setError(safeString(ex?.message) || t('The file could not be downloaded.'));
    }
  }, [customizationID, definition, recordID]);

  const stale = !!result && ranDefinition !== definition;
  const dataStoreRowCount = Array.isArray(result?.DataStoreRows) ? result.DataStoreRows.length : null;
  const sampleCount = (() => { try { return JSON.parse(samplesJson).length; } catch { return 0; } })();
  const mappingRowCount = fieldMap?.rows?.length ?? 0;
  const worksheetProductCount = Object.keys(productMappings ?? {}).length;

  // the panel's body is measured; this preview is a flex column, so take the pane's height from the strip's parent
  const bodyRef = React.useRef(null);
  const [bodyHeight, setBodyHeight] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setBodyHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tabs = [
    { key: 'run', label: t('Run'), tag: 'supplyOrderPreviewTabRun' },
    { key: 'mapping', label: mappingRowCount > 0 ? `${t('Mapping')} (${mappingRowCount})` : t('Mapping'), tag: 'supplyOrderPreviewTabMapping' },
    { key: 'products', label: worksheetProductCount > 0 ? `${t('Products')} (${worksheetProductCount})` : t('Products'), tag: 'supplyOrderPreviewTabProducts' },
  ];
  const tabStyle = (active) => ({
    border: 'none', borderBottom: active ? '2px solid #6f42c1' : '2px solid transparent', background: 'none', cursor: 'pointer',
    fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? '#4c2a85' : '#475569', padding: '4px 10px',
  });

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, flexWrap: 'wrap' }}>
        <div role="tablist" data-tagname="supplyOrderPreviewTabs" style={{ display: 'flex', gap: 2 }}>
          {tabs.map((x) => (
            <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} data-tagname={x.tag} style={tabStyle(tab === x.key)} onClick={() => setTab(x.key)}>
              {x.label}
            </button>
          ))}
        </div>
        {tab === 'run' && (
          <>
            <AccentButton tagName="supplyOrderIntegrationRun" disabled={running || busy || sessionOutputs.length === 0} onClick={run}
              tooltip={t('Run the current scripts against the test supply job. Nothing is sent and nothing is decrypted.')}>
              {running ? t('Running...') : t('Run')}
            </AccentButton>
            <span style={{ color: '#6c757d' }}>
              {recordID > 0
                ? `${t('Test supply job')}: ${safeString(hostContext?.recordReference) || recordID}`
                : t('Sample order (pick a test supply job in the editor to run against real data)')}
              {sessionQueries.length > 0 ? ` \u00b7 ${t('{{count}} query(ies)', { count: sessionQueries.length })}` : ''}
              {dataStoreRowCount !== null ? ` \u00b7 ${t('{{count}} data store row(s) requested', { count: dataStoreRowCount })}` : ''}
              {` \u00b7 ${t('{{count}} sample reply(ies)', { count: sampleCount })}`}
              {stale ? ` \u00b7 ${t('The scripts changed since this run.')}` : ''}
            </span>
          </>
        )}
        {tab === 'mapping' && <span style={{ color: '#6c757d' }}>{t('where each documented supplier field comes from')}</span>}
        {tab === 'products' && (
          <span style={{ color: '#6c757d' }}>
            {canEditWorksheet
              ? t('Live against this chat: the plan and the worksheet as the assistant records them. Picks made here go back to the editor with the session.')
              : t('The plan and the worksheet as the assistant records them.')}
          </span>
        )}
      </div>
      {error && (
        <div style={{ color: '#b02a37', fontSize: 12.5, whiteSpace: 'pre-wrap', background: '#fff5f5', borderRadius: 4, padding: '6px 8px' }}>{error}</div>
      )}
      <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflow: tab === 'mapping' ? 'auto' : 'hidden', display: tab === 'run' ? 'flex' : 'block', flexDirection: 'column', background: '#f1f3f5', borderRadius: 4, padding: tab === 'products' ? 0 : 8 }}>
        {tab === 'run' && (result ? (
          <SupplyOrderIntegrationPreviewPanels result={result} onDownload={download} />
        ) : (
          <div style={{ padding: 16, color: '#6c757d', fontSize: 13 }}>
            {running
              ? t('Running...')
              : (sessionOutputs.length === 0 || accentUtils.isEmpty(definition)
                ? t('Ask the assistant for the integration and the produced files, send plan and sample replies appear here.')
                : t('Press Run to see what the integration produces.'))}
          </div>
        ))}
        {tab === 'mapping' && (mappingRowCount > 0 ? (
          <div data-tagname="supplyOrderMappingPanel" style={{ background: '#fff', borderRadius: 4, padding: 8, border: '1px solid #e2e8f0' }}>
            <FieldMapTable fieldMap={fieldMap} stale={fieldMapStale} compact />
          </div>
        ) : (
          <div style={{ padding: 16, color: '#6c757d', fontSize: 13 }}>
            {t('No mapping table yet. The assistant records one with the dispatch script (the "artifact: fieldMap" block).')}
          </div>
        ))}
        {tab === 'products' && (
          <div style={{ background: '#fff', borderRadius: 4, padding: 8, height: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <React.Suspense fallback={<div style={{ padding: 16 }}><AccentSpinner /></div>}>
              <SupplyOrderProductMappingsPanel
                customizationID={customizationID}
                supplierID={supplierID}
                supplierName={supplierName}
                recordID={recordID}
                mappingPlan={mappingPlan}
                productMappings={productMappings}
                onProductMappingsChange={canEditWorksheet ? writeWorksheet : undefined}
                getDefinition={getDefinition}
                initialProductID={hostContext?.productMappingsProductID ?? null}
                onSuggest={onSendPrompt ? (e, product) => onSendPrompt(suggestMappingsPrompt(product, supplierName), SUGGEST_MAPPINGS_INTENT) : undefined}
                onValidate={onSendPrompt ? (e, product) => onSendPrompt(validateMappingsPrompt(product, supplierName), VALIDATE_MAPPINGS_INTENT) : undefined}
                onError={(message) => setError(safeString(message))}
                disabled={busy}
                height={Math.max(bodyHeight - 16, 240)}
              />
            </React.Suspense>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplyOrderIntegrationPreview;
